// What it costs to have RENTED scaffold sitting on a job — the daily rate the
// owner pays a supplier for gear physically allocated to the site, tracked on
// the Rentals tab as equipment_locations rows (section 'Rental') against
// equipment.daily_rate. Job Costs' Equipment group only ever read
// equipment_costs (rows an admin types by hand, monthly_rate), so every piece
// of rented scaffold sent to a job was real cash out that no screen showed.
// This is supplier spend, not the owner's own gear, so it is its own group on
// the statement rather than merged into the existing Equipment rows.
//
// Days follow the exact shape equipment_costs already uses in renderJobCosts
// (_ecDaysBetween, js/admin/65-equipment-costs.js): the span runs from
// start_date to end_date, or to today while the piece is still on site.
// Unlike _ecDaysBetween this does NOT add the +1/clamp-to-1 equipment_costs
// uses for its "at least one billed day" convention — a scaffold allocation
// that started today has been on site 0 days so far, and the figure keeps
// climbing while it sits there, which is the whole point of reading this
// live instead of from a typed monthly line.
//
// Pure arithmetic on plain objects: no DOM, no network, no Supabase.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
// An allocation still on site has no end: later than any date typed.
const OPEN = '9999-12-31';
const dayNum = (d) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / 86400000;
const dayStr = (n) => new Date(n * 86400000).toISOString().slice(0, 10);

/**
 * Whole days an allocation has been (or was) on site: least(end_date ?? today,
 * today) − start_date, floored at 0. Null — never 0 — when the row cannot be
 * dated at all (no start_date on file, which happens in production). Callers
 * must treat null as "cannot price this row", not as zero days.
 */
export function scaffoldDaysBetween(startDate, endDate, todayStr) {
  if (!startDate) return null;
  const today = todayStr || new Date().toISOString().slice(0, 10);
  const end = endDate && endDate < today ? endDate : today;
  const days = Math.round((new Date(end + 'T00:00:00') - new Date(startDate + 'T00:00:00')) / 86400000);
  return Math.max(0, days);
}

/**
 * A daily rental rate as the text an input shows: two decimals, and more only
 * when the rate needs them (up to six). '' for no rate on file.
 *
 * Suppliers quote per 28 days, so a cheap piece has a daily rate under a cent
 * — $1.00 per 28 days is $0.035714 a day. Cut to two decimals that was shown,
 * and then SAVED, as $0.04 (12% high); a 28-day price under $0.14 was saved
 * as $0.00, which scaffoldRowCost reads as "no price on file". Six decimals
 * put the 28-day price back to the cent it was typed at.
 */
export function dailyRateText(rate) {
  if (rate == null || rate === '') return '';
  const n = Number(rate);
  if (!Number.isFinite(n)) return '';
  const full = String(Number(n.toFixed(6)));
  return /\.\d{3,}$/.test(full) ? full : n.toFixed(2);
}

/**
 * One allocation row's cost and pricing state.
 *
 * A daily_rate of null OR 0 is treated as "no rate on file". Whether a TYPED
 * $0.00 should instead mean "free" is an open question for the owner
 * (2026-10-01): the Rentals modal stores a blank box as null, so a stored 0
 * was typed — but until that same day the modal also rounded any 28-day price
 * under $0.14 down to 0, so a stored 0 is not proof of intent either. The
 * rule is unchanged until he answers. A row with no start_date cannot be dated. Either way
 * the row prices at $0 — never NaN, never a silent contribution to cost —
 * and is flagged (hasRate / dated) so the caller can surface it rather than
 * drop it.
 *
 * 2026-08-07 — pct_in_house RETIRED (inherited from the old TM app; the
 * owner never wanted a "the sub eats the rest" split). Every allocation now
 * counts 100% into job cost, full stop. The column stays on the row (no
 * migration, no rewrite) and callers may still pass it in, but it is never
 * read here — reversible by just re-adding the multiply if that ever changes.
 */
export function scaffoldRowCost(row, todayStr) {
  const r = row || {};
  const quantity = num(r.quantity);
  const rate = r.daily_rate == null ? null : num(r.daily_rate);
  const hasRate = rate != null && rate > 0;
  const days = scaffoldDaysBetween(r.start_date, r.end_date, todayStr);
  const dated = days != null;
  const cost = (dated && hasRate) ? days * quantity * rate : 0;
  return { days: dated ? days : 0, cost, hasRate, dated };
}

/**
 * Roll a site's equipment_locations rows (already filtered to section
 * 'Rental' and the project's matching site) into the Job Costs figure.
 *
 * Returns the total cost, each row with its own days/cost/hasRate/dated
 * merged in (for a per-piece breakdown in the statement), and the two
 * "cannot price this" lists — noRate, noDate — folded into one `unpriced`
 * count so a hidden pricing gap never reads on screen as "no scaffold on
 * this job" when the truth is "some of it couldn't be priced".
 */
export function scaffoldRentalCost(rows, todayStr) {
  const priced = [];
  const noRate = [];
  const noDate = [];
  let cost = 0;
  (rows || []).forEach(r => {
    const c = scaffoldRowCost(r, todayStr);
    cost += c.cost;
    const out = { ...r, ...c };
    priced.push(out);
    if (!c.dated) noDate.push(out);
    else if (!c.hasRate) noRate.push(out);
  });
  return { cost, rows: priced, noRate, noDate, unpriced: noRate.length + noDate.length };
}

/**
 * The other rows on the job that carry the SAME start date as row `id` — or,
 * for a row with no start date, the other rows that have none either. Rows
 * with nothing on site (quantity 0) are left out: they cost nothing and the
 * statement does not print them, so the count matches what the admin sees.
 *
 * Why it exists: gear entered in one sitting carries one date on every piece
 * (the day the inventory was loaded, on a tenant that started with a backlog),
 * so correcting a job's arrival date is one decision, not one per piece.
 */
export function sameStartRows(rows, id) {
  const list = rows || [];
  const row = list.find(r => r.id === id);
  if (!row) return [];
  const key = row.start_date || null;
  return list.filter(r => r.id !== id && (r.start_date || null) === key && num(r.quantity) > 0);
}

/**
 * What an edit of ONE allocation's start date writes — Job Costs' scaffold row
 * (owner's decision, 2026-10-02: the date gear arrived is a fact only the
 * tenant has, and every screen that places gear stamps today).
 *
 *   { error, ids, skipped, overlaps }
 *
 * error    'missing'   the row is no longer in the list
 *          'invalid'   not a YYYY-MM-DD date
 *          'afterEnd'  later than the day the row left the site — days would
 *                      be negative. The same day is allowed: start = end is
 *                      zero days, the honest way to say "it was never here".
 *          null        go ahead
 * ids      the rows to write: this one, plus — with `alsoSameStart` — every
 *          sameStartRows sibling the new date fits
 * skipped  siblings that left the site BEFORE the new date. They are not
 *          touched: the edit never invents a date for a row nobody looked at.
 *
 * overlaps written rows whose NEW range would share a billed day with another
 *          row of the same piece on the job — see below. Never an error: the
 *          caller asks, the admin decides.
 *
 * No limit on the date itself, past or future: a start still ahead simply has
 * cost nothing yet (scaffoldDaysBetween floors at 0).
 *
 * OVERLAPS. The Rentals tab closes a row and opens a new one on every quantity
 * change, so most pieces have an ended range and an open one on the same site,
 * back to back (Feb 3 → Jun 3, then Jun 3 → today). Moving the open row's
 * start back over the ended one bills the same gear twice for those days, and
 * the save used to go through in silence. Each entry is
 *   { id, other, name, from, to }
 * — the written row, the row it would overlap, and that OTHER row's dates
 * (`to` null = still on site), which is what the question names. Rules:
 *   · the same piece is the same equipment_id, never the same name (two
 *     catalogue rows can share a name); a row without one is never matched;
 *   · STRICT: a row bills the days BETWEEN its dates, so a pair that closes
 *     and reopens on one day shares no billed day and is not an overlap;
 *   · only rows that are NOT being rewritten by this same save are compared
 *     against, and rows with nothing on site (quantity 0) are ignored.
 */
export function startDateEdit(rows, id, newDate, alsoSameStart) {
  const none = (error) => ({ error, ids: [], skipped: 0, overlaps: [] });
  const row = (rows || []).find(r => r.id === id);
  if (!row) return none('missing');
  // The value of a date input: a real date or ''.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate || '') || isNaN(new Date(newDate + 'T00:00:00'))) return none('invalid');
  if (row.end_date && newDate > row.end_date) return none('afterEnd');
  const same = alsoSameStart ? sameStartRows(rows, id) : [];
  const fits = same.filter(r => !r.end_date || newDate <= r.end_date);
  const ids = [id, ...fits.map(r => r.id)];
  const written = new Set(ids);
  const others = (rows || []).filter(r => !written.has(r.id) && r.equipment_id && r.start_date && num(r.quantity) > 0);
  const overlaps = [];
  [row, ...fits].forEach(w => {
    if (!w.equipment_id || !(num(w.quantity) > 0)) return;
    const o = others.find(x => x.equipment_id === w.equipment_id
      && newDate < (x.end_date || OPEN) && x.start_date < (w.end_date || OPEN));
    if (o) overlaps.push({ id: w.id, other: o.id, name: w.name || '', from: o.start_date, to: o.end_date || null });
  });
  return { error: null, ids, skipped: same.length - fits.length, overlaps };
}

/**
 * The same piece on the job in two rows at once: rows of ONE equipment_id
 * whose billed ranges share a day — what startDateEdit asks about before a
 * save, found again afterwards (a confirmed overlap, or one saved before the
 * question existed, stays in the total with nothing else printed).
 *
 * One entry per piece: { equipment_id, name, ids, from, to, days, amount }.
 * A row's range is [start, end or today) — it bills the days BETWEEN its
 * dates, so back-to-back rows never match. Only rows that cost something
 * count: a quantity, a daily rate and a start date. `amount` is per day two
 * or more rows are on site, every quantity but the largest × the daily rate:
 * what goes away if they are the same gear (two rows = shared days × the
 * smaller quantity). `from` / `to` are the first shared day and the day the
 * sharing ends, as the rows themselves print their dates.
 */
export function scaffoldOverlaps(rows, todayStr) {
  const today = todayStr || new Date().toISOString().slice(0, 10);
  const byPiece = {};
  (rows || []).forEach(r => {
    if (!r || !r.equipment_id || !r.start_date || !(num(r.quantity) > 0) || !(num(r.daily_rate) > 0)) return;
    const s = dayNum(r.start_date);
    const e = dayNum(r.end_date && r.end_date < today ? r.end_date : today);
    if (!(s < e)) return;                                   // zero days billed (or not arrived yet)
    (byPiece[r.equipment_id] = byPiece[r.equipment_id] || []).push({ id: r.id, name: r.name || '', s, e,
      qty: num(r.quantity), rate: num(r.daily_rate) });
  });
  return Object.entries(byPiece).map(([equipment_id, rs]) => {
    if (rs.length < 2) return null;
    const hit = new Set();
    let days = 0, amount = 0, first = null, last = null;
    const end = Math.max(...rs.map(r => r.e));
    for (let d = Math.min(...rs.map(r => r.s)); d < end; d++) {
      const on = rs.filter(r => r.s <= d && d < r.e);
      if (on.length < 2) continue;
      on.forEach(r => hit.add(r.id));
      days += 1;
      amount += (on.reduce((a, r) => a + r.qty, 0) - Math.max(...on.map(r => r.qty))) * on[0].rate;
      if (first == null) first = d;
      last = d;
    }
    return days ? { equipment_id, name: rs[0].name, ids: rs.filter(r => hit.has(r.id)).map(r => r.id),
                    from: dayStr(first), to: dayStr(last + 1), days, amount } : null;
  }).filter(Boolean);
}

/**
 * Which PIECES cannot be priced — distinct catalogue items, by name — out of
 * rows that already carry hasRate / dated (scaffoldRentalCost's rows, or the
 * statement items built from them).
 *
 * The note on Job Costs used to count ROWS. A row is one date range of one
 * item on site, so a single unpriced item moved on and off the job four times
 * read as "4 pieces have no price on file" — four things to fix where there
 * was one. A piece is counted once however many ranges it has.
 *
 * The two reasons are kept apart because the fix is different: `noRate` needs
 * a price typed on the Rentals tab, `noDate` needs a start date. A piece with
 * neither is listed under noRate only — the price is the first thing missing.
 *
 * What counts as "no rate" is NOT decided here: it is scaffoldRowCost's
 * hasRate (null or 0), unchanged.
 */
export function unpricedPieces(rows) {
  const noRate = new Set(), noDate = new Set();
  (rows || []).forEach(r => {
    const name = String(r?.name || '').trim();
    if (!r?.hasRate) noRate.add(name);
    else if (!r.dated) noDate.add(name);
  });
  noRate.forEach(n => noDate.delete(n));
  return { noRate: [...noRate], noDate: [...noDate] };
}
